import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import aiApi, { toAiEmail, streamAiTask } from '../../utils/aiApi';
import { renderMarkdown } from '../../utils/miniMarkdown';
import { getMailPassword } from '../../utils/mailCredentials';
import './ai.css';

/**
 * AiAssistantPanel
 * A Gmail-Gemini / Copilot-style AI side panel that appears for every opened
 * email. Renders as a floating launcher + slide-in drawer so it works across
 * every folder view without disturbing existing layout. Each tab loads its
 * result independently; text tasks stream token-by-token.
 */

const TABS = [
  { id: 'summary', label: 'Summary', stream: false, icon: 'M4 6h16M4 12h10M4 18h7' },
  { id: 'reply', label: 'Reply', stream: true, icon: 'M3 10l9-7 9 7v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z' },
  { id: 'translate', label: 'Translate', stream: true, icon: 'M4 5h7M9 3v2c0 4-2 7-5 9M5 9c0 3 3 5 6 6M13 19l4-9 4 9M14.5 16h5' },
  { id: 'rewrite', label: 'Rewrite', stream: true, icon: 'M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z' },
  { id: 'grammar', label: 'Grammar', stream: true, icon: 'M4 7V4h16v3M9 20h6M12 4v16' },
  { id: 'tasks', label: 'Tasks', stream: false, icon: 'M9 11l3 3L22 4M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11' },
  { id: 'meeting', label: 'Meetings', stream: false, icon: 'M8 7V3M16 7V3M3 11h18M5 5h14a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2z' },
  { id: 'security', label: 'Security', stream: false, icon: 'M12 2l8 4v6c0 5-3.5 8-8 10-4.5-2-8-5-8-10V6z' },
  { id: 'attachment', label: 'Attachment', stream: false, icon: 'M21 12.5V17a4 4 0 0 1-8 0V7a2.5 2.5 0 0 1 5 0v9' },
];

const emptySlice = () => ({ loading: false, streaming: false, text: '', data: null, error: '', meta: null });

const Spinner = () => <span className="ai-spinner" aria-hidden="true" />;

const Skeleton = () => (
  <div className="ai-skeleton" aria-hidden="true">
    <div className="ai-skel-line w80" />
    <div className="ai-skel-line w100" />
    <div className="ai-skel-line w60" />
    <div className="ai-skel-line w90" />
    <div className="ai-skel-line w40" />
  </div>
);

function useEmailKey(email) {
  return useMemo(() => {
    if (!email) return '';
    return String(email._id || email.uid || email.imapUid || email.messageId || email.subject || Math.random());
  }, [email]);
}

const LAUNCHER_POS_KEY = 'aiLauncherPos';
const DRAG_THRESHOLD = 6;

function clampLauncherPos(x, y, width = 72, height = 48) {
  const pad = 8;
  const maxX = Math.max(pad, window.innerWidth - width - pad);
  const maxY = Math.max(pad, window.innerHeight - height - pad);
  return {
    x: Math.min(Math.max(pad, x), maxX),
    y: Math.min(Math.max(pad, y), maxY),
  };
}

function loadLauncherPos() {
  try {
    const raw = localStorage.getItem(LAUNCHER_POS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.x !== 'number' || typeof parsed?.y !== 'number') return null;
    return clampLauncherPos(parsed.x, parsed.y);
  } catch (_) {
    return null;
  }
}

/** Drag-anywhere floating AI launcher; click opens, drag moves + persists. */
function useDraggableLauncher(onOpen) {
  const [pos, setPos] = useState(() => loadLauncherPos());
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef(null);
  const btnRef = useRef(null);

  useEffect(() => {
    const keepInView = () => {
      setPos((prev) => {
        if (!prev) return prev;
        const el = btnRef.current;
        const w = el?.offsetWidth || 72;
        const h = el?.offsetHeight || 48;
        const next = clampLauncherPos(prev.x, prev.y, w, h);
        if (next.x === prev.x && next.y === prev.y) return prev;
        localStorage.setItem(LAUNCHER_POS_KEY, JSON.stringify(next));
        return next;
      });
    };
    window.addEventListener('resize', keepInView);
    return () => window.removeEventListener('resize', keepInView);
  }, []);

  const onPointerDown = useCallback((e) => {
    if (e.button != null && e.button !== 0) return;
    const el = btnRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    // First drag: convert default bottom-right into left/top coords.
    const startX = pos?.x ?? rect.left;
    const startY = pos?.y ?? rect.top;
    dragRef.current = {
      pointerId: e.pointerId,
      startClientX: e.clientX,
      startClientY: e.clientY,
      originX: startX,
      originY: startY,
      width: rect.width,
      height: rect.height,
      moved: false,
    };
    el.setPointerCapture?.(e.pointerId);
    e.preventDefault();
  }, [pos]);

  const onPointerMove = useCallback((e) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    const dx = e.clientX - drag.startClientX;
    const dy = e.clientY - drag.startClientY;
    if (!drag.moved && (Math.abs(dx) > DRAG_THRESHOLD || Math.abs(dy) > DRAG_THRESHOLD)) {
      drag.moved = true;
      setDragging(true);
    }
    if (!drag.moved) return;
    const next = clampLauncherPos(drag.originX + dx, drag.originY + dy, drag.width, drag.height);
    setPos(next);
  }, []);

  const endDrag = useCallback((e) => {
    const drag = dragRef.current;
    if (!drag || (e.pointerId != null && drag.pointerId !== e.pointerId)) return;
    const wasMoved = drag.moved;
    dragRef.current = null;
    setDragging(false);
    const el = btnRef.current;
    el?.releasePointerCapture?.(drag.pointerId);
    if (wasMoved) {
      setPos((current) => {
        const saved = current || clampLauncherPos(drag.originX, drag.originY, drag.width, drag.height);
        localStorage.setItem(LAUNCHER_POS_KEY, JSON.stringify(saved));
        return saved;
      });
      return;
    }
    onOpen?.();
  }, [onOpen]);

  const style = pos
    ? { left: pos.x, top: pos.y, right: 'auto', bottom: 'auto' }
    : undefined;

  return {
    btnRef,
    style,
    dragging,
    handlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp: endDrag,
      onPointerCancel: endDrag,
    },
  };
}

export default function AiAssistantPanel({ email, currentUser: currentUserProp, isImap: isImapProp }) {
  const navigate = useNavigate();
  const emailKey = useEmailKey(email);

  const currentUser = useMemo(() => {
    if (currentUserProp) return currentUserProp;
    try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch (_) { return null; }
  }, [currentUserProp]);
  const isImap = typeof isImapProp === 'boolean'
    ? isImapProp
    : (email?.source === 'imap' || Boolean(email?.uid || email?.imapUid));

  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('summary');
  const [store, setStore] = useState({});
  const [settings, setSettings] = useState(null);
  const [settingsError, setSettingsError] = useState('');
  const [theme, setTheme] = useState(() => localStorage.getItem('aiPanelTheme') || 'dark');

  // Per-tab controls.
  const [replyTone, setReplyTone] = useState('professional');
  const [replyInstructions, setReplyInstructions] = useState('');
  const [translateLang, setTranslateLang] = useState('English');
  const [rewriteStyle, setRewriteStyle] = useState('professional');

  const abortRef = useRef(null);
  const aiEmail = useMemo(() => toAiEmail(email), [email]);
  const openPanel = useCallback(() => setOpen(true), []);
  const launcher = useDraggableLauncher(openPanel);

  // Reset all cached results whenever a different email is opened.
  useEffect(() => {
    setStore({});
    abortRef.current?.();
  }, [emailKey]);

  useEffect(() => () => abortRef.current?.(), []);

  // Load settings once (enable state + option lists).
  useEffect(() => {
    let active = true;
    aiApi.getSettings()
      .then((res) => { if (active && res?.success) setSettings(res.data); })
      .catch((e) => { if (active) setSettingsError(e.response?.data?.message || e.message); });
    return () => { active = false; };
  }, []);

  const setSlice = useCallback((tabId, patch) => {
    setStore((prev) => ({ ...prev, [tabId]: { ...(prev[tabId] || emptySlice()), ...patch } }));
  }, []);

  const languages = settings?.languages || ['English', 'Kannada', 'Hindi', 'Malayalam', 'Tamil', 'Arabic', 'French', 'German', 'Japanese', 'Spanish'];
  const replyTones = settings?.replyTones || ['professional', 'friendly', 'short', 'formal', 'custom'];
  const rewriteStyles = settings?.rewriteStyles || ['professional', 'friendly', 'formal', 'simple', 'executive', 'marketing', 'sales', 'technical'];

  // ---- Task runners --------------------------------------------------------

  const runBuffered = useCallback(async (tabId, fn) => {
    setSlice(tabId, { loading: true, error: '', data: null, text: '' });
    try {
      const res = await fn();
      if (!res?.success) throw new Error(res?.message || 'AI request failed');
      setSlice(tabId, { loading: false, data: res.data, meta: res.data.meta });
    } catch (e) {
      setSlice(tabId, { loading: false, error: e.response?.data?.message || e.message || 'AI request failed' });
    }
  }, [setSlice]);

  const runStream = useCallback((tabId, task, payload) => {
    abortRef.current?.();
    setSlice(tabId, { loading: true, streaming: true, error: '', text: '', data: null, meta: null });
    abortRef.current = streamAiTask(task, payload, {
      onMeta: (m) => setSlice(tabId, { meta: { model: m.model } }),
      onDelta: (delta) => setStore((prev) => {
        const slice = prev[tabId] || emptySlice();
        return { ...prev, [tabId]: { ...slice, loading: false, text: slice.text + delta } };
      }),
      onDone: (done) => setSlice(tabId, { streaming: false, loading: false, meta: done }),
      onError: (e) => setSlice(tabId, { streaming: false, loading: false, error: e.message }),
    });
  }, [setSlice]);

  const runTab = useCallback((tabId, force = false) => {
    const existing = store[tabId];
    if (existing && !force && (existing.loading || existing.streaming || existing.data || existing.text)) return;

    switch (tabId) {
      case 'summary':
        return runBuffered(tabId, () => aiApi.summary(aiEmail));
      case 'reply':
        return runStream(tabId, 'reply', {
          email: aiEmail,
          tone: replyTone,
          instructions: replyInstructions,
          senderName: currentUser?.firstName ? `${currentUser.firstName} ${currentUser.lastName || ''}`.trim() : '',
        });
      case 'translate':
        return runStream(tabId, 'translate', { email: aiEmail, text: aiEmail.body, targetLanguage: translateLang });
      case 'rewrite':
        return runStream(tabId, 'rewrite', { email: aiEmail, text: aiEmail.body, style: rewriteStyle });
      case 'grammar':
        return runStream(tabId, 'grammar', { email: aiEmail, text: aiEmail.body });
      case 'tasks':
        return runBuffered(tabId, () => aiApi.tasks(aiEmail));
      case 'meeting':
        return runBuffered(tabId, () => aiApi.meeting(aiEmail));
      case 'security':
        return runBuffered(tabId, () => aiApi.security(aiEmail));
      case 'attachment':
        return undefined; // handled per-file inside the tab
      default:
        return undefined;
    }
  }, [store, aiEmail, replyTone, replyInstructions, translateLang, rewriteStyle, currentUser, runBuffered, runStream]);

  // Auto-run the active tab once when opened (except attachment).
  useEffect(() => {
    if (!open) return;
    if (tab === 'attachment') return;
    runTab(tab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tab, emailKey]);

  const toggleTheme = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    localStorage.setItem('aiPanelTheme', next);
  };

  const insertIntoCompose = useCallback((body, { subject, replyTo } = {}) => {
    const draft = {
      subject: subject || (email?.subject ? `Re: ${email.subject.replace(/^re:\s*/i, '')}` : ''),
      body: textToHtml(body),
      to: replyTo || extractEmail(email?.from) || '',
    };
    try { sessionStorage.setItem('aiComposeDraft', JSON.stringify(draft)); } catch (_) { /* ignore */ }
    window.dispatchEvent(new CustomEvent('ai:insertCompose', { detail: draft }));
    navigate('/compose');
  }, [email, navigate]);

  if (settings && settings.enabled === false) {
    // AI disabled by admin or missing API key — hide the panel entirely.
    return null;
  }

  const slice = store[tab] || emptySlice();

  return (
    <div className={`ai-assistant-root ai-theme-${theme}`}>
      {!open && (
        <button
          type="button"
          ref={launcher.btnRef}
          className={`ai-launcher ${launcher.dragging ? 'ai-launcher-dragging' : ''}`}
          style={launcher.style}
          title="Drag to move · Click to open AI Assistant"
          aria-label="Open AI Assistant"
          {...launcher.handlers}
        >
          <SparkIcon />
          <span className="ai-launcher-text">AI</span>
        </button>
      )}

      {open && <div className="ai-backdrop" onClick={() => setOpen(false)} />}

      <aside className={`ai-panel ${open ? 'ai-open' : ''}`} aria-hidden={!open}>
        <header className="ai-panel-head">
          <div className="ai-brand">
            <SparkIcon />
            <div>
              <div className="ai-brand-title">AI Assistant</div>
              <div className="ai-brand-sub">Built into ATOZASMail</div>
            </div>
          </div>
          <div className="ai-head-actions">
            <button type="button" className="ai-icon-btn" onClick={toggleTheme} title="Toggle theme">
              {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
            </button>
            <button type="button" className="ai-icon-btn" onClick={() => setOpen(false)} title="Close" aria-label="Close">
              <CloseIcon />
            </button>
          </div>
        </header>

        <nav className="ai-tabs" role="tablist">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={tab === t.id}
              className={`ai-tab ${tab === t.id ? 'active' : ''}`}
              onClick={() => setTab(t.id)}
            >
              <svg className="ai-tab-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d={t.icon} />
              </svg>
              <span>{t.label}</span>
            </button>
          ))}
        </nav>

        <div className="ai-panel-body">
          {settingsError && <div className="ai-error">{settingsError}</div>}

          {tab === 'reply' && (
            <div className="ai-controls">
              <label className="ai-field">
                <span>Tone</span>
                <select value={replyTone} onChange={(e) => setReplyTone(e.target.value)}>
                  {replyTones.map((t) => <option key={t} value={t}>{cap(t)}</option>)}
                </select>
              </label>
              {replyTone === 'custom' && (
                <input
                  className="ai-input"
                  placeholder="Custom instructions (e.g. decline politely, propose Friday)"
                  value={replyInstructions}
                  onChange={(e) => setReplyInstructions(e.target.value)}
                />
              )}
              <button type="button" className="ai-run-btn" onClick={() => runTab('reply', true)}>Generate</button>
            </div>
          )}

          {tab === 'translate' && (
            <div className="ai-controls">
              <label className="ai-field">
                <span>Language</span>
                <select value={translateLang} onChange={(e) => setTranslateLang(e.target.value)}>
                  {languages.map((l) => <option key={l} value={l}>{l}</option>)}
                </select>
              </label>
              <button type="button" className="ai-run-btn" onClick={() => runTab('translate', true)}>Translate</button>
            </div>
          )}

          {tab === 'rewrite' && (
            <div className="ai-controls">
              <label className="ai-field">
                <span>Style</span>
                <select value={rewriteStyle} onChange={(e) => setRewriteStyle(e.target.value)}>
                  {rewriteStyles.map((s) => <option key={s} value={s}>{cap(s)}</option>)}
                </select>
              </label>
              <button type="button" className="ai-run-btn" onClick={() => runTab('rewrite', true)}>Rewrite</button>
            </div>
          )}

          {(tab === 'grammar') && (
            <div className="ai-controls">
              <button type="button" className="ai-run-btn" onClick={() => runTab('grammar', true)}>Fix &amp; Polish</button>
            </div>
          )}

          <div className="ai-result" role="tabpanel">
            {slice.error && <div className="ai-error">{slice.error}</div>}
            {slice.loading && !slice.text && <Skeleton />}

            {tab === 'summary' && <SummaryView slice={slice} onRegenerate={() => runTab('summary', true)} />}
            {tab === 'tasks' && <TasksView slice={slice} onRegenerate={() => runTab('tasks', true)} />}
            {tab === 'meeting' && <MeetingView slice={slice} onRegenerate={() => runTab('meeting', true)} email={email} />}
            {tab === 'security' && <SecurityView slice={slice} onRegenerate={() => runTab('security', true)} />}
            {tab === 'attachment' && (
              <AttachmentView email={email} isImap={isImap} setSlice={setSlice} slice={slice} />
            )}

            {['reply', 'translate', 'rewrite', 'grammar'].includes(tab) && (
              <StreamTextView
                slice={slice}
                tab={tab}
                onInsert={(body) => insertIntoCompose(body, tab === 'reply' ? {} : { subject: email?.subject })}
              />
            )}
          </div>
        </div>

        <footer className="ai-panel-foot">
          <MetaBar meta={slice.meta} usage={settings?.usage} />
        </footer>
      </aside>
    </div>
  );
}

// ---- Sub-views -------------------------------------------------------------

function ResultToolbar({ text, filename = 'ai-result.txt', onInsert }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_) { /* ignore */ }
  };
  const download = () => {
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };
  if (!text) return null;
  return (
    <div className="ai-toolbar">
      <button type="button" className="ai-chip-btn" onClick={copy}>{copied ? 'Copied!' : 'Copy'}</button>
      {onInsert && <button type="button" className="ai-chip-btn primary" onClick={() => onInsert(text)}>Insert into Compose</button>}
      <button type="button" className="ai-chip-btn" onClick={download}>Download</button>
    </div>
  );
}

function StreamTextView({ slice, tab, onInsert }) {
  if (!slice.text && !slice.loading && !slice.streaming && !slice.error) {
    return <div className="ai-hint">Choose your options and generate.</div>;
  }
  return (
    <div>
      {slice.text && (
        <div className="ai-markdown" dangerouslySetInnerHTML={{ __html: renderMarkdown(slice.text) }} />
      )}
      {slice.streaming && <span className="ai-typing"><Spinner /> generating…</span>}
      <ResultToolbar text={slice.text} filename={`ai-${tab}.txt`} onInsert={onInsert} />
    </div>
  );
}

function SummaryView({ slice, onRegenerate }) {
  const s = slice.data?.summary;
  if (!s) return null;
  const raw = s.raw;
  if (raw) return <div className="ai-markdown" dangerouslySetInnerHTML={{ __html: renderMarkdown(raw) }} />;
  return (
    <div className="ai-cards">
      {s.oneLine && <div className="ai-oneliner">{s.oneLine}</div>}
      <Section title="Key Points" items={s.keyPoints} />
      <Section title="Action Items" items={s.actionItems} accent="amber" />
      <Section title="Deadlines" items={s.deadlines} accent="red" />
      <Section title="Important People" items={s.importantPeople} />
      <LinkSection title="Important Links" items={s.importantLinks} />
      <button type="button" className="ai-chip-btn" onClick={onRegenerate}>Regenerate</button>
    </div>
  );
}

function TasksView({ slice, onRegenerate }) {
  const tasks = slice.data?.tasks;
  if (!tasks) return null;
  if (!tasks.length) return <div className="ai-hint">No tasks found in this email.</div>;
  return (
    <div className="ai-cards">
      <ul className="ai-task-list">
        {tasks.map((t, i) => (
          <li key={i} className={`ai-task ai-pri-${t.priority}`}>
            <span className="ai-task-check" />
            <div>
              <div className="ai-task-title">{t.title}</div>
              {t.due && <div className="ai-task-due">Due: {t.due}</div>}
            </div>
            <span className={`ai-badge ai-pri-badge-${t.priority}`}>{t.priority}</span>
          </li>
        ))}
      </ul>
      <button type="button" className="ai-chip-btn" onClick={onRegenerate}>Regenerate</button>
    </div>
  );
}

function MeetingView({ slice, onRegenerate, email }) {
  const m = slice.data?.meeting;
  if (!m) return null;
  if (!m.hasMeeting) return (
    <div>
      <div className="ai-hint">No meeting detected in this email.</div>
      <button type="button" className="ai-chip-btn" onClick={onRegenerate}>Re-check</button>
    </div>
  );
  const links = [
    ['Google Meet', m.googleMeetLink],
    ['Zoom', m.zoomLink],
    ['Microsoft Teams', m.teamsLink],
  ].filter(([, v]) => v);

  const downloadIcs = () => {
    aiApi.calendar(toAiEmail(email)).then((res) => {
      if (res?.success && res.data.ics) {
        const blob = new Blob([res.data.ics], { type: 'text/calendar;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'event.ics';
        a.click();
        URL.revokeObjectURL(url);
      }
    }).catch(() => {});
  };

  return (
    <div className="ai-cards">
      {m.title && <div className="ai-oneliner">{m.title}</div>}
      <div className="ai-kv">
        {m.date && <div><span>Date</span><b>{m.date}</b></div>}
        {m.time && <div><span>Time</span><b>{m.time} {m.timezone}</b></div>}
        {m.venue && <div><span>Venue</span><b>{m.venue}</b></div>}
      </div>
      {links.length > 0 && (
        <div className="ai-links">
          {links.map(([label, url]) => (
            <a key={label} href={url} target="_blank" rel="noopener noreferrer" className="ai-link-btn">{label}</a>
          ))}
        </div>
      )}
      <Section title="Attendees" items={m.attendees} />
      <Section title="Action Items" items={m.actionItems} accent="amber" />
      <div className="ai-toolbar">
        <button type="button" className="ai-chip-btn primary" onClick={downloadIcs}>Download .ics</button>
        <button type="button" className="ai-chip-btn" onClick={onRegenerate}>Regenerate</button>
      </div>
    </div>
  );
}

function SecurityView({ slice, onRegenerate }) {
  const r = slice.data?.report;
  if (!r) return null;
  return (
    <div className="ai-cards">
      <div className={`ai-risk ai-risk-${r.riskLevel}`}>
        <div className="ai-risk-score">{r.riskScore}<small>/100</small></div>
        <div className="ai-risk-level">{r.riskLevel.toUpperCase()} RISK</div>
      </div>
      {r.recommendation && <div className="ai-oneliner">{r.recommendation}</div>}
      <Section title="Reasons" items={r.reasons} />
      <LinkSection title="Suspicious URLs" items={r.suspiciousUrls} danger />
      <Section title="Fake Sender Indicators" items={r.fakeSenderIndicators} accent="red" />
      <Section title="Urgency Tricks" items={r.urgencyTricks} accent="amber" />
      <Section title="Spoofing Indicators" items={r.spoofingIndicators} accent="red" />
      <button type="button" className="ai-chip-btn" onClick={onRegenerate}>Re-analyze</button>
    </div>
  );
}

function AttachmentView({ email, isImap, setSlice, slice }) {
  const attachments = email?.attachments || [];
  const [activeIdx, setActiveIdx] = useState(null);

  const summarize = async (att, index) => {
    setActiveIdx(index);
    setSlice('attachment', { loading: true, error: '', data: null });
    try {
      const base64 = await fetchAttachmentBase64(email, index, isImap);
      const res = await aiApi.attachment({
        base64,
        mimetype: att.mimetype || '',
        filename: att.filename || `attachment-${index}`,
      });
      if (!res?.success) throw new Error(res?.message || 'Failed');
      setSlice('attachment', { loading: false, data: res.data, meta: res.data.meta });
    } catch (e) {
      setSlice('attachment', { loading: false, error: e.response?.data?.message || e.message || 'Failed to summarize attachment' });
    }
  };

  if (!attachments.length) {
    return <div className="ai-hint">This email has no attachments.</div>;
  }

  const summary = slice.data?.summary;

  return (
    <div className="ai-cards">
      <div className="ai-attach-list">
        {attachments.map((att, i) => (
          <button
            key={i}
            type="button"
            className={`ai-attach-item ${activeIdx === i ? 'active' : ''}`}
            onClick={() => summarize(att, i)}
          >
            <span className="ai-attach-name">{att.filename || `Attachment ${i + 1}`}</span>
            <span className="ai-attach-sum">Summarize</span>
          </button>
        ))}
      </div>
      {slice.loading && <Skeleton />}
      {summary && (
        <div className="ai-cards">
          {summary.oneLine && <div className="ai-oneliner">{summary.oneLine}</div>}
          {summary.summary && <div className="ai-markdown" dangerouslySetInnerHTML={{ __html: renderMarkdown(summary.summary) }} />}
          <Section title="Key Points" items={summary.keyPoints} />
          <Section title="Action Items" items={summary.actionItems} accent="amber" />
          <Section title="Entities" items={summary.entities} />
        </div>
      )}
    </div>
  );
}

// ---- Small presentational helpers -----------------------------------------

function Section({ title, items, accent }) {
  if (!items || !items.length) return null;
  return (
    <div className={`ai-section ${accent ? `ai-accent-${accent}` : ''}`}>
      <div className="ai-section-title">{title}</div>
      <ul className="ai-md-list">
        {items.map((it, i) => <li key={i}>{typeof it === 'string' ? it : JSON.stringify(it)}</li>)}
      </ul>
    </div>
  );
}

function LinkSection({ title, items, danger }) {
  if (!items || !items.length) return null;
  return (
    <div className={`ai-section ${danger ? 'ai-accent-red' : ''}`}>
      <div className="ai-section-title">{title}</div>
      <ul className="ai-md-list">
        {items.map((it, i) => (
          <li key={i}>
            <a href={it} target="_blank" rel="noopener noreferrer" className="ai-break">{it}</a>
          </li>
        ))}
      </ul>
    </div>
  );
}

function MetaBar({ meta, usage }) {
  // Intentionally omit model names from the user UI.
  const parts = [];
  if (meta?.ms) parts.push(`${meta.ms} ms`);
  return (
    <div className="ai-meta">
      <span>{parts.join(' · ') || 'AI Assistant'}</span>
      {usage && usage.perUserTokenLimit > 0 && (
        <span className="ai-usage">{Math.round((usage.userTokens / usage.perUserTokenLimit) * 100)}% daily used</span>
      )}
    </div>
  );
}

// ---- Icons -----------------------------------------------------------------

const SparkIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
    <path d="M12 2l1.8 5.4L19 9.2l-5.2 1.8L12 16l-1.8-5L5 9.2l5.2-1.8z" />
    <path d="M19 14l.9 2.6L22.5 17l-2.6.9L19 20l-.9-2.1L15.5 17l2.6-.4z" opacity="0.7" />
  </svg>
);
const CloseIcon = () => <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>;
const SunIcon = () => <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M2 12h2M20 12h2M5 5l1.5 1.5M17.5 17.5L19 19M5 19l1.5-1.5M17.5 6.5L19 5" /></svg>;
const MoonIcon = () => <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21 12.8A9 9 0 1 1 11.2 3 7 7 0 0 0 21 12.8z" /></svg>;

// ---- Utilities -------------------------------------------------------------

function cap(s) { return String(s || '').charAt(0).toUpperCase() + String(s || '').slice(1); }
function extractEmail(from) {
  if (!from) return '';
  const m = String(from).match(/<([^>]+)>/);
  return m ? m[1] : String(from).trim();
}
function textToHtml(text) {
  if (!text) return '';
  // If it already looks like HTML, keep it; otherwise convert newlines.
  if (/<[a-z][\s\S]*>/i.test(text)) return text;
  return text
    .split(/\n{2,}/)
    .map((p) => `<p>${p.replace(/\n/g, '<br/>')}</p>`)
    .join('');
}

async function fetchAttachmentBase64(mail, attachmentIndex, isImap) {
  const apiBase = process.env.REACT_APP_API_URL || '';
  const partIndex = mail?.attachments?.[attachmentIndex]?.index ?? attachmentIndex;
  const uid = mail.uid || mail.imapUid;
  const url = isImap && uid
    ? `${apiBase}/api/mail/server/message/${uid}/attachment/${partIndex}?download=1`
    : `${apiBase}/api/mail/${mail._id}/attachment/${partIndex}?download=1`;
  const headers = {};
  const token = localStorage.getItem('token');
  const mailPassword = getMailPassword();
  if (token) headers.Authorization = `Bearer ${token}`;
  if (mailPassword) headers['x-mail-password'] = mailPassword;
  const response = await fetch(url, { headers });
  if (!response.ok) throw new Error('Failed to fetch attachment');
  const blob = await response.blob();
  return blobToBase64(blob);
}

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result || '';
      resolve(String(result).split(',')[1] || '');
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}
